/**
 * NavigatorTextConversation — text chat with Navigator AI advisor.
 * Same context as voice Navigator, uses Claude Messages API.
 * Pattern matches AI participant meeting chat (MeetingChat.tsx).
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

const API_BASE = import.meta.env.VITE_API_URL ?? ''

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}

interface Message {
  role: 'user' | 'assistant'
  content: string
}

interface Props {
  simId: string
  conversationId: string
  systemPrompt: string
  firstMessage: string
  conversationType: string
  onEnd: (transcript: string, duration: number) => void
}

export function NavigatorTextConversation({
  simId, conversationId, systemPrompt, firstMessage, conversationType, onEnd,
}: Props) {
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: firstMessage },
  ])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const startTime = useRef(Date.now())

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text || sending) return

    const userMsg: Message = { role: 'user', content: text }
    const updatedMessages = [...messages, userMsg]
    setMessages(updatedMessages)
    setInput('')
    setSending(true)

    try {
      const token = await getToken()
      const resp = await fetch(`${API_BASE}/api/sim/${simId}/navigator/text-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          role_id: '',  // not needed for text — context already in system_prompt
          conversation_id: conversationId,
          message: text,
          messages_so_far: updatedMessages.slice(0, -1),  // exclude the message we just sent
          system_prompt: systemPrompt,
        }),
      })

      if (resp.ok) {
        const json = await resp.json()
        const reply = json.data?.response || 'Sorry, I could not process that.'
        setMessages(prev => [...prev, { role: 'assistant', content: reply }])
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Connection issue. Please try again.' }])
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection issue. Please try again.' }])
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }, [input, sending, messages, simId, conversationId, systemPrompt])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleEnd = () => {
    const transcript = messages.map(m =>
      `${m.role === 'user' ? 'You' : 'Navigator'}: ${m.content}`
    ).join('\n\n')
    const duration = Math.round((Date.now() - startTime.current) / 1000)
    onEnd(transcript, duration)
  }

  const typeLabel = conversationType === 'intro' ? 'Preparation' :
    conversationType === 'outro' ? 'Reflection' : 'Advisory'

  return (
    <div className="fixed inset-0 z-50 bg-card flex flex-col md:items-center md:justify-center">
      <div className="w-full h-full md:max-w-[600px] md:h-[85vh] md:rounded-xl md:border md:border-border md:shadow-2xl flex flex-col bg-card overflow-hidden">

        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-lg">🧭</span>
            <div>
              <h3 className="font-heading text-body-sm font-semibold text-text-primary">Navigator</h3>
              <span className="font-body text-[10px] text-text-secondary">{typeLabel} · Text</span>
            </div>
          </div>
          <button
            onClick={handleEnd}
            className="font-body text-caption text-danger/70 hover:text-danger transition-colors"
          >
            End Conversation
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
          style={{ WebkitOverflowScrolling: 'touch' as never }}>
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                msg.role === 'user'
                  ? 'bg-action/15 text-text-primary'
                  : 'bg-base text-text-primary border border-border'
              }`}>
                <p className="font-body text-body-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-base border border-border rounded-2xl px-4 py-2.5">
                <span className="font-body text-body-sm text-text-secondary animate-pulse">Navigator is thinking...</span>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="px-3 py-3 border-t border-border shrink-0 safe-area-bottom">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Navigator anything..."
              rows={1}
              className="flex-1 bg-base border border-border rounded-2xl px-4 py-2.5 font-body text-text-primary resize-none focus:border-action/50 focus:outline-none transition-colors max-h-32 overflow-y-auto"
              style={{ minHeight: '42px', fontSize: '16px' }}
              disabled={sending}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className="bg-action text-white rounded-full w-10 h-10 flex items-center justify-center shrink-0 disabled:opacity-30 transition-opacity"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
